import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import confetti from 'canvas-confetti';
import { usePusher } from '../../contexts/PusherContext';
import SignupGlobe from './SignupGlobe';
import {
  playHooray,
  readSoundPref,
  soundReady,
  soundSupported,
  unlockSound,
  writeSoundPref,
} from './signupSound';
import {
  GROWTH_SIGNUP_EVENT,
  POLL_MS,
  addSignups,
  globeActive,
  signupPlace,
  unseenSignups,
  type GrowthSignup,
} from './liveSignups';
import { Line } from 'react-chartjs-2';
import api from '../../api';
import { GrowthSourceRow, channelRows, rollingSum } from './growthMetrics';
import { lineOpts } from './scaling/scalingCard';
import './growth-dashboard.scss';

/**
 * Headlines → Growth: one full-screenable board.
 *   Left:  new users by channel for the last N days, with week-over-week pills
 *          (MQL1 = onboarding session started; Onboarded = completed onboarding,
 *          counted on the completion day).
 *   Right: the total of users who have completed onboarding, and completions
 *          as a rolling N-day sum.
 * Sources come from /activation-stats/growth; channels are the Acquisition
 * tab's grouping (acquisitionFunnel.ts).
 */

const DAY_OPTIONS = [7, 14, 30] as const;

interface GrowthResponse {
  success: boolean;
  days: number;
  from: string;
  to: string;
  bySource: GrowthSourceRow[];
  dailyOnboarded: Array<{ day: string; onboarded: number }>;
  completedUsers: number;
}

const Delta: FC<{ cur: number; prev: number }> = ({ cur, prev }) => {
  if (prev === 0) {
    return (
      <span className={`growth-pill ${cur > 0 ? 'up' : 'flat'}`}>
        {cur > 0 ? 'new' : '—'}
      </span>
    );
  }
  const pct = Math.round(((cur - prev) / prev) * 100);
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <span className={`growth-pill ${dir}`}>
      {pct > 0 ? '+' : ''}
      {pct}%
    </span>
  );
};

type PusherChannel = {
  bind: (event: string, cb: (data: GrowthSignup) => void) => void;
  unbind: (event: string, cb: (data: GrowthSignup) => void) => void;
};

const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const GrowthDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [days, setDays] = useState<number>(7);
  const [data, setData] = useState<GrowthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const pusher = usePusher(true) as { opsChannel?: PusherChannel } | null;
  const opsChannel = pusher?.opsChannel ?? null;
  const [signups, setSignups] = useState<GrowthSignup[]>([]);
  const [lastSignupAt, setLastSignupAt] = useState<number | null>(null);
  const [soundOn, setSoundOn] = useState<boolean>(readSoundPref);
  // Browsers only allow audio after a click/keypress on the page.
  const [soundArmed, setSoundArmed] = useState<boolean>(soundReady);
  const soundOnRef = useRef(soundOn);
  useEffect(() => {
    soundOnRef.current = soundOn;
    writeSoundPref(soundOn);
  }, [soundOn]);

  useEffect(() => {
    if (!soundOn || soundArmed || !soundSupported()) return;
    let cancelled = false;
    const arm = async () => {
      if ((await unlockSound()) && !cancelled) setSoundArmed(true);
    };
    arm();
    document.addEventListener('pointerdown', arm);
    document.addEventListener('keydown', arm);
    return () => {
      cancelled = true;
      document.removeEventListener('pointerdown', arm);
      document.removeEventListener('keydown', arm);
    };
  }, [soundOn, soundArmed]);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GrowthResponse>(
        `/activation-stats/growth?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch {
      setError('Failed to load growth stats');
    }
  }, [getAccessTokenSilently, days]);

  useEffect(() => {
    load();
  }, [load]);

  const signupsRef = useRef<GrowthSignup[]>([]);
  useEffect(() => {
    signupsRef.current = signups;
  }, [signups]);
  const seededRef = useRef(false);

  const fetchRecentSignups = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const res = await api.get<{ signups: GrowthSignup[] }>(
      '/activation-stats/growth/recent-signups',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data.signups;
  }, [getAccessTokenSilently]);

  // A new sign-up: celebrate, pin it on the globe, refresh the numbers.
  const celebrate = useCallback(
    (arrivals: GrowthSignup[]) => {
      if (arrivals.length === 0) return;
      setSignups((list) => addSignups(list, arrivals));
      setLastSignupAt(Date.now());
      setNow(Date.now());
      const burst = (x: number, angle: number) =>
        confetti({
          particleCount: 160,
          spread: 70,
          angle,
          startVelocity: 55,
          origin: { x, y: 0.7 },
          zIndex: 10000,
          colors: ['#16a34a', '#4f46e5', '#f59e0b', '#ec4899'],
        });
      burst(0.1, 60);
      burst(0.9, 120);
      setTimeout(() => burst(0.5, 90), 250);
      if (soundOnRef.current) playHooray();
      load();
    },
    [load],
  );

  // Seed the feed on load, then poll as a fallback for a sleeping or dropped
  // Pusher connection: anything newer than what we have is treated as live.
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const fetched = await fetchRecentSignups();
        if (cancelled) return;
        if (!seededRef.current) {
          seededRef.current = true;
          setSignups(fetched);
          return;
        }
        celebrate(unseenSignups(signupsRef.current, fetched));
      } catch {
        /* feed is optional */
      } finally {
        inFlight = false;
      }
    };
    poll();
    const t = setInterval(poll, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchRecentSignups, celebrate]);

  useEffect(() => {
    if (!opsChannel) return;
    const onSignup = (signup: GrowthSignup) => {
      if (signupsRef.current.some((s) => s.sessionId === signup.sessionId))
        return;
      celebrate([signup]);
    };
    opsChannel.bind(GROWTH_SIGNUP_EVENT, onSignup);
    return () => {
      opsChannel.unbind(GROWTH_SIGNUP_EVENT, onSignup);
    };
  }, [opsChannel, celebrate]);

  // Tick so the globe hands back to the chart two minutes after the last one.
  useEffect(() => {
    if (!globeActive(lastSignupAt, now)) return;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [lastSignupAt, now]);
  const showGlobe = globeActive(lastSignupAt, now);

  useEffect(() => {
    const onChange = () =>
      setFullscreen(document.fullscreenElement === boardRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      boardRef.current?.requestFullscreen?.();
    }
  };

  const rows = useMemo(() => (data ? channelRows(data.bySource) : []), [data]);
  const total = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          started: acc.started + r.started,
          startedPrev: acc.startedPrev + r.startedPrev,
          onboarded: acc.onboarded + r.onboarded,
          onboardedPrev: acc.onboardedPrev + r.onboardedPrev,
        }),
        { started: 0, startedPrev: 0, onboarded: 0, onboardedPrev: 0 },
      ),
    [rows],
  );
  const rolling = useMemo(
    () => (data ? rollingSum(data.dailyOnboarded, data.days) : []),
    [data],
  );

  const chartData = {
    labels: rolling.map((p) => fmtDay(p.day)),
    datasets: [
      {
        data: rolling.map((p) => p.value),
        borderColor: '#4f46e5',
        backgroundColor: 'rgba(79, 70, 229, 0.12)',
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHitRadius: 8,
      },
    ],
  };

  const windowLabel = data
    ? `${fmtDay(data.from.slice(0, 10))} – ${fmtDay(data.to.slice(0, 10))}`
    : '';

  return (
    <div
      className={`growth-dashboard${fullscreen ? ' fullscreen' : ''}`}
      ref={boardRef}
    >
      <div className='growth-head'>
        <div>
          <h2>Growth</h2>
          <p className='growth-sub'>
            Last {days} days{windowLabel ? ` (${windowLabel})` : ''} vs the{' '}
            {days} days before
          </p>
        </div>
        <div className='growth-controls'>
          <div className='day-selector'>
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type='button'
                className={`day-btn ${days === d ? 'active' : ''}`}
                onClick={() => setDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>
          {soundSupported() && (
            <button
              type='button'
              className={`growth-sound ${soundOn ? 'on' : ''}`}
              onClick={() => setSoundOn((v) => !v)}
              title={soundOn ? 'Mute sign-up sound' : 'Play a sound on sign-up'}
              aria-pressed={soundOn}
            >
              {soundOn ? '🔊' : '🔇'}
              {soundOn && !soundArmed && (
                <span className='growth-sound-hint'>click to enable sound</span>
              )}
            </button>
          )}
          <button
            type='button'
            className='growth-fullscreen'
            onClick={toggleFullscreen}
          >
            {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {error && <div className='msg error'>{error}</div>}
      {!data && !error && <div className='loading'>Loading...</div>}

      {data && (
        <div className='growth-grid'>
          <section className='growth-card'>
            <h3>New users by channel</h3>
            <table className='growth-table'>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th className='num'>MQL1</th>
                  <th className='num'>Onboarded</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <i
                        className='growth-dot'
                        style={{ background: r.color }}
                      />
                      {r.label}
                    </td>
                    <td className='num'>
                      {r.started} <Delta cur={r.started} prev={r.startedPrev} />
                    </td>
                    <td className='num'>
                      {r.onboarded}{' '}
                      <Delta cur={r.onboarded} prev={r.onboardedPrev} />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={3} className='growth-empty'>
                      No sign-ups in this window
                    </td>
                  </tr>
                )}
                <tr className='total'>
                  <td>Total</td>
                  <td className='num'>
                    {total.started}{' '}
                    <Delta cur={total.started} prev={total.startedPrev} />
                  </td>
                  <td className='num'>
                    {total.onboarded}{' '}
                    <Delta cur={total.onboarded} prev={total.onboardedPrev} />
                  </td>
                </tr>
              </tbody>
            </table>
            <p className='growth-note'>
              MQL1 = onboarding session started (the Acquisition tab&apos;s MQL
              column; includes invited people who haven&apos;t replied yet).
              Onboarded = completed onboarding, counted on the day they
              completed. Pills compare with the previous {days} days.
            </p>
          </section>

          <div className='growth-right'>
            <section className='growth-card growth-total'>
              <div>
                <div className='growth-big'>{data.completedUsers}</div>
                <div className='growth-big-label'>
                  Total users who completed onboarding
                </div>
              </div>
              <div className='growth-week'>
                last {days} days
                <b>+{total.onboarded}</b>
              </div>
            </section>
            <section className='growth-card growth-chart'>
              {showGlobe ? (
                <>
                  <h3>
                    <span className='growth-live-dot' /> New sign-up ·{' '}
                    {signups[0] ? signupPlace(signups[0]) : ''}
                  </h3>
                  <div className='growth-chart-area growth-globe'>
                    <SignupGlobe signups={signups} />
                  </div>
                </>
              ) : (
                <>
                  <h3>Onboarded users · rolling {data.days}-day total</h3>
                  <div className='growth-chart-area'>
                    <Line data={chartData} options={lineOpts} />
                  </div>
                </>
              )}
            </section>
            {signups.length > 0 && (
              <section className='growth-card growth-feed'>
                <h3>Just signed up</h3>
                <ul>
                  {signups.map((s) => (
                    <li key={s.sessionId}>
                      <div>
                        <b>{signupPlace(s)}</b>
                        <span>{s.sourceLabel}</span>
                      </div>
                      <time dateTime={s.completedAt}>
                        {new Date(s.completedAt).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </time>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GrowthDashboard;
